// NOMBASE - Saved Suppliers Context (API-Driven)
// Handles brand's saved/potential supplier list with backend persistence
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { savedSuppliersAPI } from '@/services/api';
import { useAuth } from './AuthContext';

const SavedSuppliersContext = createContext(null);

export const useSavedSuppliers = () => {
  const context = useContext(SavedSuppliersContext);
  if (!context) throw new Error('useSavedSuppliers must be used within SavedSuppliersProvider');
  return context;
};

export const SavedSuppliersProvider = ({ children }) => {
  const { isAuthenticated, isBrand } = useAuth();
  const [savedSuppliers, setSavedSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load saved suppliers from API (brands only). Deferred off the
  // critical boot path with requestIdleCallback so it doesn't compete
  // with the page's own data fetches on first paint — consumers already
  // tolerate the brief empty-initial state.
  useEffect(() => {
    if (!isAuthenticated || !isBrand) return;
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      setLoading(true);
      savedSuppliersAPI.getAll()
        .then(data => { if (!cancelled) setSavedSuppliers(Array.isArray(data) ? data : []); })
        .catch(() => { if (!cancelled) setSavedSuppliers([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    };
    const ric = window.requestIdleCallback;
    const handle = ric ? ric(run, { timeout: 2000 }) : setTimeout(run, 600);
    return () => {
      cancelled = true;
      if (ric && window.cancelIdleCallback) window.cancelIdleCallback(handle);
      else clearTimeout(handle);
    };
  }, [isAuthenticated, isBrand]);

  const saveSupplier = useCallback(async (supplier) => {
    // Optimistic local update
    const tempEntry = { id: `temp-${Date.now()}`, supplier_id: supplier.id, supplier_name: supplier.name };
    setSavedSuppliers(prev => {
      if (prev.some(s => s.supplier_id === supplier.id)) return prev;
      return [...prev, tempEntry];
    });

    try {
      const saved = await savedSuppliersAPI.save(supplier.id, null);
      // Replace temp with real
      setSavedSuppliers(prev => prev.map(s => s.id === tempEntry.id ? saved : s));
    } catch (err) {
      // If already saved, just refresh
      if (err.message?.includes('already saved')) {
        return;
      }
      // Remove temp on failure
      setSavedSuppliers(prev => prev.filter(s => s.id !== tempEntry.id));
    }
  }, []);

  const removeSavedSupplier = useCallback(async (supplierId) => {
    const prev = savedSuppliers;
    setSavedSuppliers(p => p.filter(s => s.supplier_id !== supplierId));
    try {
      await savedSuppliersAPI.remove(supplierId);
    } catch {
      setSavedSuppliers(prev); // Rollback
    }
  }, [savedSuppliers]);

  const updateSavedSupplier = useCallback(async (supplierId, data) => {
    await savedSuppliersAPI.update(supplierId, data);
    // Refresh list
    const fresh = await savedSuppliersAPI.getAll();
    setSavedSuppliers(Array.isArray(fresh) ? fresh : []);
  }, []);

  const isSupplierSaved = useCallback((supplierId) => {
    return savedSuppliers.some(s => s.supplier_id === supplierId);
  }, [savedSuppliers]);

  const refreshSavedSuppliers = useCallback(async () => {
    try {
      const data = await savedSuppliersAPI.getAll();
      setSavedSuppliers(Array.isArray(data) ? data : []);
    } catch {
      // ignore
    }
  }, []);

  const value = {
    savedSuppliers,
    saveSupplier,
    removeSavedSupplier,
    updateSavedSupplier,
    isSupplierSaved,
    refreshSavedSuppliers,
    savedSuppliersLoading: loading,
  };

  return (
    <SavedSuppliersContext.Provider value={value}>
      {children}
    </SavedSuppliersContext.Provider>
  );
};
